import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

interface SectionTheme {
  id: string;
  bg: string;
  text: string;
  buttonBg: string;
  buttonText: string;
}

const sectionThemes: SectionTheme[] = [
  { id: "hero", bg: "bg-off-white", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "problem", bg: "bg-vivid-pink", text: "text-near-black", buttonBg: "bg-near-black", buttonText: "text-vivid-pink" },
  { id: "use-cases", bg: "bg-off-white", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "product", bg: "bg-off-white", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "cost", bg: "bg-near-black", text: "text-off-white", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "setup", bg: "bg-off-white", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "icp", bg: "bg-warm-light", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "faq", bg: "bg-off-white", text: "text-near-black", buttonBg: "bg-signal-yellow", buttonText: "text-near-black" },
  { id: "cta", bg: "bg-vivid-pink", text: "text-near-black", buttonBg: "bg-near-black", buttonText: "text-vivid-pink" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState(sectionThemes[0]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const sy = window.scrollY;
        setScrolled(sy > 20);
        const scrollY = sy + 60;
        for (let i = sectionThemes.length - 1; i >= 0; i--) {
          const el = document.getElementById(sectionThemes[i].id);
          if (el && scrollY >= el.offsetTop) {
            setTheme(sectionThemes[i]);
            break;
          }
        }
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNavLink = (to: string) => {
    setMobileOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? `${theme.bg} border-b border-black/5` : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className={`wordmark text-xl md:text-2xl tracking-tight transition-colors duration-500 ${theme.text}`}
          >
            alvo.
          </Link>
          <div className="hidden md:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
            <button
              onClick={() => scrollTo("product")}
              className={`text-sm font-body transition-colors duration-500 ${theme.text} hover:opacity-60`}
            >
              Product
            </button>
            <button
              onClick={() => scrollTo("use-cases")}
              className={`text-sm font-body transition-colors duration-500 ${theme.text} hover:opacity-60`}
            >
              Use Cases
            </button>
            <Link
              to="/blog"
              className={`text-sm font-body transition-colors duration-500 ${theme.text} hover:opacity-60`}
            >
              Blog
            </Link>
          </div>

          {/* Desktop CTA */}
          <button
            data-tally-open="BzB7q5"
            data-tally-layout="modal"
            data-tally-width="400"
            data-tally-emoji-text="👋"
            data-tally-emoji-animation="wave"
            className={`group relative hidden md:block text-sm font-body font-semibold px-5 py-2.5 rounded-sm transition-all duration-500 hover:brightness-110 ${theme.buttonBg} ${theme.buttonText}`}
          >
            Get early access<span className="absolute -top-3 -right-3 text-xl opacity-0 group-hover:opacity-100 group-hover:animate-pop-in">🎯</span>
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden transition-colors duration-500 ${theme.text}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-near-black pt-20"
          >
            <div className="flex flex-col items-center gap-8 pt-12">
              <button
                onClick={() => scrollTo("product")}
                className="text-lg font-body text-off-white hover:text-signal-yellow transition-colors"
              >
                Product
              </button>
              <button
                onClick={() => scrollTo("use-cases")}
                className="text-lg font-body text-off-white hover:text-signal-yellow transition-colors"
              >
                Use Cases
              </button>
              <Link
                to="/blog"
                onClick={() => handleNavLink("/blog")}
                className="text-lg font-body text-off-white hover:text-signal-yellow transition-colors"
              >
                Blog
              </Link>
              <button
                data-tally-open="BzB7q5"
                data-tally-layout="modal"
                data-tally-width="400"
                data-tally-emoji-text="👋"
                data-tally-emoji-animation="wave"
                className="group relative mt-4 font-body font-semibold bg-signal-yellow text-near-black px-8 py-4 text-base rounded-sm hover:brightness-110 transition-all"
              >
                Get early access<span className="absolute -top-4 -right-4 text-2xl opacity-0 group-hover:opacity-100 group-hover:animate-pop-in">🎯</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
